import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ChatTelegram } from "../../types/chat-telegram.types";
import { MessageTelegram } from "../../types/last-message.types";
import {
  SendImageQueryParams,
  SendMessageQueryParams,
  SendVideoQueryParams
} from "../../types/telegram-page.types";
import { TelegramChatWindowService } from "../../services/telegram-chat-window.service";
import { TelegramFileUrlService } from "../../services/telegram-file-url.service";
import { TelegramAccountsService } from "../../services/telegram-accounts.service";
import { SelectData } from "../../../../../core/types/records-table.types";
import { TelegramGroupedMessagesService } from "../../services/telegram-grouped-messages.service";
import { TelegramSelectDataService } from "../../services/telegram-select-data.service";
import {
  TelegramSelectRecordCalendarIdComponent
} from "./components/telegram-select-record-calendar-id/telegram-select-record-calendar-id.component";
import { TelegramMessageCardComponent } from "./components/telegram-message-card/telegram-message-card.component";
import {
  TelegramChatUploadFileComponent
} from "./components/telegram-chat-upload-file/telegram-chat-upload-file.component";
import { TelegramSendMessageComponent } from "./components/telegram-send-message/telegram-send-message.component";

@Component({
  selector: 'app-telegram-chat-window',
  standalone: true,
  imports: [
    TelegramSelectRecordCalendarIdComponent,
    TelegramMessageCardComponent,
    TelegramChatUploadFileComponent,
    TelegramSendMessageComponent
  ],
  templateUrl: './telegram-chat-window.component.html',
  styleUrl: './telegram-chat-window.component.scss'
})
export class TelegramChatWindowComponent implements OnChanges, AfterViewChecked {
  @Input({ required: true }) selectedChat: ChatTelegram | null = null;
  @Input({ required: true }) messages: Array<MessageTelegram> | null = null;
  @Input() recordCalendarId: number = 0;
  @Input() isModal: boolean = false;
  @Output() sendMessage: EventEmitter<SendMessageQueryParams> = new EventEmitter<SendMessageQueryParams>();
  @Output() sendImgMessage: EventEmitter<SendImageQueryParams> = new EventEmitter<SendImageQueryParams>();
  @Output() sendVideoMessage: EventEmitter<SendVideoQueryParams> = new EventEmitter<SendVideoQueryParams>();
  @ViewChild('chatContainer') private chatContainer!: ElementRef;

  public telegramChatWindowService: TelegramChatWindowService = inject(TelegramChatWindowService);
  public telegramGroupedMessagesService: TelegramGroupedMessagesService = inject(TelegramGroupedMessagesService);
  private telegramFileUrlService: TelegramFileUrlService = inject(TelegramFileUrlService);
  private telegramAccountsService: TelegramAccountsService = inject(TelegramAccountsService);
  private telegramSelectDataService: TelegramSelectDataService = inject(TelegramSelectDataService);

  public recordCalenderIdSelectData: Array<SelectData> = [];
  private previousMessagesLength: number = 0;

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if ('selectedChat' in changes) {
      await this.handleSelectedChatChange(this.selectedChat);
    }

    if ('messages' in changes) {
      this.telegramGroupedMessagesService.updateMessages(this.messages);
    }
  }

  public ngAfterViewChecked(): void {
    if (!this.selectedChat) { return; }
    if (this.messages?.length === this.previousMessagesLength) { return; }

    if (this.messages?.length) {
      this.telegramFileUrlService.loadImageMessages({
        messages: this.messages,
        accountId: this.telegramAccountsService.selectedAccount().accountId
      });
    }

    this.previousMessagesLength = this.messages?.length ?? 0;
    this.scrollToBottom();
  }

  public getHeader(): string {
    if (!this.selectedChat || !this.telegramAccountsService.selectedAccount()) {
      return 'Чат не выбран';
    }

    return `${this.telegramAccountsService.selectedAccount().accountName} с ${this.selectedChat.title} `
  }

  public hasMessagesForDate(date: string, recordCalendarId: number): boolean {
    const messages = this.telegramGroupedMessagesService.groupedMessages()[date] || [];
    return messages.some(msg =>
      !recordCalendarId || msg.recordCalendarId === recordCalendarId
    );
  }

  public handelSendMessage(sendMessageQueryParams: SendMessageQueryParams): void {
    this.sendMessage.emit(sendMessageQueryParams);
  }

  public attachRecordCalendarId(recordCalendarId: number): void {
    this.recordCalendarId = recordCalendarId;
  }

  private async handleSelectedChatChange(selectedChat: ChatTelegram | null): Promise<void> {
    this.telegramGroupedMessagesService.clear();

    if (selectedChat) {
      await this.getSelectData();
    } else {
      this.recordCalenderIdSelectData = [];
    }

    if (!this.isModal) {
      this.recordCalendarId = 0;
    }

    this.isModal = false;
  }

  private async getSelectData(): Promise<void> {
    this.recordCalenderIdSelectData = [];
    this.recordCalenderIdSelectData = await this.telegramSelectDataService.getSelectRecordCalendarIdData(this.selectedChat!.id, this.telegramAccountsService.selectedAccount().accountId).catch(() => []);
  }

  private scrollToBottom(): void {
    this.telegramChatWindowService.scrollToBottom(this.chatContainer);
  }
}
